/**
 * LAB-47: brand -> expert routing.
 *
 * Questions route on brand alone. The market/country dimension in the original
 * story was dropped by the business, so the mapping is brand-wide.
 */

export enum BrandExpertRole {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

/**
 * Categories the requester picks so the expert knows what is being asked.
 * Deliberately separate from the AI-derived classification used in the
 * analytics export - neither maps onto the other.
 */
export enum ExpertQuestionType {
  TECHNICAL_OR_APPLICATION = "technical_or_application",
  COMPETITIVE_ANALYSIS = "competitive_analysis",
  AFTERSALES_AND_SUPPORT = "aftersales_and_support",
  DOCUMENT_CHECK_PROOF = "document_check_proof",
  OTHERS = "others",
}

export const EXPERT_QUESTION_TYPE_LABELS: Record<ExpertQuestionType, string> = {
  [ExpertQuestionType.TECHNICAL_OR_APPLICATION]: "Technical or Application",
  [ExpertQuestionType.COMPETITIVE_ANALYSIS]: "Competitive Analysis",
  [ExpertQuestionType.AFTERSALES_AND_SUPPORT]: "Aftersales and Support",
  [ExpertQuestionType.DOCUMENT_CHECK_PROOF]: "Document Check / Proof",
  [ExpertQuestionType.OTHERS]: "Others",
}

export const EXPERT_QUESTION_TYPE_OPTIONS = Object.entries(
  EXPERT_QUESTION_TYPE_LABELS
).map(([name, value]) => ({ name: name as ExpertQuestionType, value }))

export type RoutableBrand = {
  id: string
  name: string
}

export type BrandExpertMapping = {
  id: string
  companyId: string
  companyName?: string
  userId: string
  market: string | null
  role: BrandExpertRole
  priority: number
  active: boolean
  createdAt: string
  updatedAt: string
  expert: {
    id: string
    name: string
    email: string
  } | null
}

/** Who a question about a given brand will go to, previewed before submitting. */
export type BrandExpertResolution = {
  companyId: string
  companyName: string
  expert: {
    id: string
    name: string
    email: string
  } | null
}
